// src/utils/templateSample.js
//
// A template's sample manuscript, as editor HTML.
//
// The Template Gallery's "Preview Sample" opens the app's own Book Preview, so
// the sample has to be what that preview reads: the editor's document markup.
// It is built the way `create_draft` builds a new draft from the same template
// (`backend/utils/template_blocks.py`) — one heading per section, marked up
// exactly as the editor marks a heading up — with sample writing under the
// sections that have some, so the preview shows what an author actually gets
// rather than a mockup of what they might wish for.
//
// The writing itself lives in `src/components/templates/templateSamples.js`.
// It is trusted, static HTML written for this purpose, never user input, so it
// is placed as given. The section titles come from the catalogue API and are
// escaped like any other text.
//
// Pagination is not done here: the page this returns is the single page the
// server seeds, and the gallery runs it through `reflowPages` — the editor's own
// layout — to cut it into sheets.

// Mirrors HEADING_STYLES in src/pages/EditorPage.jsx and `_HEADING_STYLE` in
// backend/utils/template_blocks.py, so a sample heading is the same block an
// author gets from the template or the toolbar.
const HEADING_STYLE = {
  1: 'font-size: 24px; font-weight: 700; font-family: Arial; text-align: left;',
  2: 'font-size: 20px; font-weight: 700; font-family: Arial; text-align: left;',
};

const END_SPACER = '<p class="rv-end-spacer" contenteditable="false"><br/></p>';

export function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * One sample block as editor markup. A block is one of:
 *   'html'                 a paragraph
 *   { h2: 'html' }         a second-level heading
 *   { lines: ['html', …] } one paragraph broken into lines (a stanza, a heading block)
 *   { center: 'html' }     a centred paragraph (a scene break, a paper's title)
 */
export function blockHtml(block, id) {
  const attr = `data-block-id="${id}"`;
  if (typeof block === 'string') return `<p ${attr}>${block}</p>`;
  if (block && typeof block.h2 === 'string') {
    return `<p data-heading-level="2" ${attr} style="${HEADING_STYLE[2]}">${block.h2}</p>`;
  }
  if (block && Array.isArray(block.lines)) return `<p ${attr}>${block.lines.join('<br>')}</p>`;
  if (block && typeof block.center === 'string') return `<p ${attr} style="text-align: center;">${block.center}</p>`;
  return '';
}

/**
 * Every block of the sample, in order: each section's heading, then its sample
 * writing — or the one empty paragraph the server seeds when there is none.
 *
 * @param {{title: string}[]} sections the template's sections, from the catalogue
 * @param {Object<number, Array>} writing sample blocks keyed by section index
 * @returns {string} the blocks' HTML, no page wrapper
 */
export function buildSampleBlocks(sections, writing = {}) {
  let n = 0;
  const id = () => `sample-${(n += 1)}`;
  return (sections || [])
    .map((section, i) => {
      const title = typeof section?.title === 'string' ? section.title.trim() : '';
      if (!title) return '';
      const heading = `<p data-heading-level="1" data-block-id="${id()}" style="${HEADING_STYLE[1]}">${escapeHtml(title)}</p>`;
      const blocks = Array.isArray(writing[i]) && writing[i].length
        ? writing[i].map((b) => blockHtml(b, id())).join('')
        : `<p data-block-id="${id()}"><br/></p>`;
      return heading + blocks;
    })
    .join('');
}

/**
 * The page body's HTML for a sample: the blocks plus the trailing spacer every
 * editor page body carries, which `reflowPages` keeps at the bottom of each page.
 */
export function sampleBodyHtml(sections, writing) {
  return `${buildSampleBlocks(sections, writing)}${END_SPACER}`;
}
